import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, Upload, Image as ImageIcon, Video, Type, 
  Sparkles, Send, Clock, Facebook, Instagram, Twitter, 
  Youtube, CheckCircle2, Loader2, Zap, X, Copy, Download,
  ExternalLink
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// Platform posting URLs
const PLATFORM_URLS = {
  instagram: 'https://www.instagram.com/',
  facebook: 'https://www.facebook.com/',
  twitter: 'https://twitter.com/compose/tweet',
  youtube: 'https://studio.youtube.com/channel/UC/videos/upload',
  tiktok: 'https://www.tiktok.com/upload'
};

const DailyDropCreate = () => {
  const navigate = useNavigate();
  const [postType, setPostType] = useState('image'); // image, video, text
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [cta, setCta] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState(['instagram']);
  const [addWatermark, setAddWatermark] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasPostedToday, setHasPostedToday] = useState(false);
  const [userPlan, setUserPlan] = useState('free');
  const [showPostSuccess, setShowPostSuccess] = useState(false);

  useEffect(() => {
    checkTodayStatus();
    // Check URL params for pre-filled content from AI Generator
    const params = new URLSearchParams(window.location.search);
    if (params.get('caption')) setCaption(params.get('caption'));
    if (params.get('hashtags')) setHashtags(params.get('hashtags'));
    if (params.get('cta')) setCta(params.get('cta'));
  }, []);

  const checkTodayStatus = async () => {
    try {
      const response = await api.get('/daily-drop/stats');
      setHasPostedToday(response.data.hasPostedToday);
      const userRes = await api.get('/auth/me');
      setUserPlan(userRes.data.plan || 'free');
    } catch (error) {
      console.error('Error checking status:', error);
    }
  };

  const handleMediaUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setMediaFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setMediaPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
  };

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev => 
      prev.includes(platform) 
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const copyFullPost = () => {
    const fullPost = `${caption}\n\n${hashtags}${cta ? `\n\n${cta}` : ''}`;
    navigator.clipboard.writeText(fullPost);
    toast.success('Full post copied! Now paste it on your platform.');
  };

  const downloadMedia = () => {
    if (!mediaPreview) {
      toast.error('No media to download');
      return;
    }
    const link = document.createElement('a');
    link.href = mediaPreview;
    link.download = `dailydrop-${Date.now()}.${mediaFile?.type?.includes('video') ? 'mp4' : 'jpg'}`;
    link.click();
    toast.success('Media downloaded!');
  };

  const openPlatform = (platformId) => {
    const url = PLATFORM_URLS[platformId];
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleSubmit = async (action = 'publish') => {
    if (!caption.trim()) {
      toast.error('Please add a caption');
      return;
    }

    if (postType !== 'text' && !mediaFile) {
      toast.error('Please upload an image or video');
      return;
    }

    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    // Check if free user already posted today
    if (hasPostedToday && userPlan === 'free' && action === 'publish') {
      toast.error("You've already dropped today! Come back tomorrow or upgrade to Pro for unlimited scheduling.");
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('caption', caption);
      formData.append('hashtags', hashtags);
      formData.append('cta', cta);
      formData.append('post_type', postType);
      formData.append('platforms', JSON.stringify(selectedPlatforms));
      formData.append('add_watermark', addWatermark);
      formData.append('action', action);
      
      if (mediaFile) {
        formData.append('media', mediaFile);
      }

      const response = await api.post('/daily-drop/posts', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        toast.success(action === 'publish' 
          ? "Content saved! 🎉 Now share it to your platforms!" 
          : "Post saved as draft");
        setShowPostSuccess(true);
      }
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setIsSubmitting(false);
    }
  };

  const platforms = [
    { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'bg-blue-600' },
    { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'bg-gradient-to-r from-pink-500 to-purple-500' },
    { id: 'twitter', name: 'X', icon: Twitter, color: 'bg-gray-800' },
    { id: 'youtube', name: 'YouTube', icon: Youtube, color: 'bg-red-600' },
    { id: 'tiktok', name: 'TikTok', icon: () => <span>📱</span>, color: 'bg-gray-900' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Header */}
      <header className="border-b border-purple-500/20 bg-black/30 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              className="text-purple-200 hover:text-white hover:bg-purple-500/20"
              onClick={() => navigate('/daily-drop')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">Create Your Daily Drop</h1>
              <p className="text-xs text-purple-300">Share your content with the world</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="border-purple-500/50 text-purple-200 hover:bg-purple-500/20"
              onClick={() => navigate('/daily-drop/ai-generator')}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              AI Generator
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Today Status Banner */}
        {hasPostedToday && userPlan === 'free' && (
          <div className="mb-6 p-4 bg-amber-500/20 border border-amber-500/30 rounded-lg flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-amber-400" />
            <div className="flex-1">
              <p className="text-amber-200 font-medium">You've already dropped today! 🔥</p>
              <p className="text-amber-300/70 text-sm">You can still create drafts or upgrade to Pro for unlimited scheduling.</p>
            </div>
            <Button 
              size="sm"
              className="bg-gradient-to-r from-amber-500 to-orange-500"
              onClick={() => navigate('/subscription')}
            >
              Upgrade
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Media Upload */}
          <div className="md:col-span-2 space-y-6">
            {/* Post Type Selector */}
            <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
              <CardContent className="p-4">
                <div className="flex gap-2">
                  {[
                    { id: 'image', label: 'Image', icon: ImageIcon },
                    { id: 'video', label: 'Video', icon: Video },
                    { id: 'text', label: 'Text Only', icon: Type },
                  ].map((type) => (
                    <Button
                      key={type.id}
                      variant={postType === type.id ? 'default' : 'outline'}
                      className={postType === type.id 
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 flex-1' 
                        : 'border-purple-500/30 text-purple-200 flex-1'}
                      onClick={() => setPostType(type.id)}
                    >
                      <type.icon className="h-4 w-4 mr-2" />
                      {type.label}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Media Upload Area */}
            {postType !== 'text' && (
              <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
                <CardContent className="p-6">
                  {mediaPreview ? (
                    <div className="relative">
                      {postType === 'image' ? (
                        <img src={mediaPreview} alt="Preview" className="w-full rounded-lg max-h-96 object-contain" />
                      ) : (
                        <video src={mediaPreview} controls className="w-full rounded-lg max-h-96" />
                      )}
                      <Button
                        size="icon"
                        variant="destructive"
                        className="absolute top-2 right-2"
                        onClick={removeMedia}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                      {addWatermark && (
                        <div className="absolute bottom-4 right-4 bg-black/50 px-2 py-1 rounded text-xs text-white/70">
                          TerryLBPrompts.com
                        </div>
                      )}
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-purple-500/30 rounded-lg cursor-pointer hover:border-purple-400/50 transition-colors">
                      <Upload className="h-12 w-12 text-purple-400 mb-4" />
                      <p className="text-purple-200 font-medium">Drop your {postType} here</p>
                      <p className="text-purple-300/60 text-sm">or click to browse</p>
                      <input
                        type="file"
                        accept={postType === 'image' ? 'image/*' : 'video/*'}
                        className="hidden"
                        onChange={handleMediaUpload}
                      />
                    </label>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Caption */}
            <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white text-lg">Caption</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Write a compelling caption..."
                  className="min-h-32 bg-purple-500/10 border-purple-500/30 text-white placeholder:text-purple-300/50"
                />
                <div className="flex justify-between mt-2">
                  <span className="text-purple-300/60 text-sm">{caption.length} characters</span>
                  <Button 
                    variant="link" 
                    className="text-purple-400 p-0 h-auto"
                    onClick={() => navigate('/daily-drop/ai-generator')}
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    Generate with AI
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Hashtags & CTA */}
            <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label className="text-purple-200">Hashtags</Label>
                  <Input
                    value={hashtags}
                    onChange={(e) => setHashtags(e.target.value)}
                    placeholder="#viral #content #creator"
                    className="bg-purple-500/10 border-purple-500/30 text-white placeholder:text-purple-300/50"
                  />
                </div>
                <div>
                  <Label className="text-purple-200">Call to Action (optional)</Label>
                  <Input
                    value={cta}
                    onChange={(e) => setCta(e.target.value)}
                    placeholder="Link in bio! 👆"
                    className="bg-purple-500/10 border-purple-500/30 text-white placeholder:text-purple-300/50"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Settings */}
          <div className="space-y-6">
            {/* Platforms */}
            <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white text-lg">Platforms</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {platforms.map((platform) => (
                  <button
                    key={platform.id}
                    onClick={() => togglePlatform(platform.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                      selectedPlatforms.includes(platform.id)
                        ? 'border-purple-500 bg-purple-500/20'
                        : 'border-purple-500/20 hover:border-purple-500/40'
                    }`}
                  >
                    <div className={`${platform.color} p-2 rounded-lg`}>
                      {typeof platform.icon === 'function' ? <platform.icon /> : <platform.icon className="h-4 w-4 text-white" />}
                    </div>
                    <span className="text-white flex-1 text-left">{platform.name}</span>
                    {selectedPlatforms.includes(platform.id) && (
                      <CheckCircle2 className="h-5 w-5 text-purple-400" />
                    )}
                  </button>
                ))}
                <p className="text-purple-300/50 text-xs text-center mt-2">
                  Platform connections coming soon
                </p>
              </CardContent>
            </Card>

            {/* Settings */}
            <Card className="bg-black/30 border-purple-500/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white text-lg">Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-purple-200">Add Watermark</Label>
                    <p className="text-purple-300/50 text-xs">TerryLBPrompts.com</p>
                  </div>
                  <Switch
                    checked={addWatermark}
                    onCheckedChange={setAddWatermark}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="space-y-3">
              {!showPostSuccess ? (
                <>
                  <Button
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 h-12"
                    onClick={() => handleSubmit('publish')}
                    disabled={isSubmitting || (hasPostedToday && userPlan === 'free')}
                    data-testid="publish-button"
                  >
                    {isSubmitting ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : (
                      <Send className="h-5 w-5 mr-2" />
                    )}
                    {hasPostedToday && userPlan === 'free' ? "Already Posted Today" : "Save & Get Share Links"}
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-purple-500/50 text-purple-200 hover:bg-purple-500/20 h-12"
                    onClick={() => handleSubmit('draft')}
                    disabled={isSubmitting}
                    data-testid="draft-button"
                  >
                    <Clock className="h-5 w-5 mr-2" />
                    Save as Draft
                  </Button>
                </>
              ) : (
                <>
                  {/* Success State - Copy & Share Options */}
                  <Card className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle2 className="h-5 w-5 text-green-400" />
                        <span className="text-green-300 font-medium">Content Ready!</span>
                      </div>
                      <p className="text-green-200/70 text-sm mb-4">
                        Copy your content and share it on your favorite platforms
                      </p>
                    </CardContent>
                  </Card>

                  {/* Copy Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      className="border-purple-500/50 text-purple-200 hover:bg-purple-500/20"
                      onClick={() => copyToClipboard(caption, 'Caption')}
                      data-testid="copy-caption"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Caption
                    </Button>
                    <Button
                      variant="outline"
                      className="border-purple-500/50 text-purple-200 hover:bg-purple-500/20"
                      onClick={() => copyToClipboard(hashtags, 'Hashtags')}
                      data-testid="copy-hashtags"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Hashtags
                    </Button>
                  </div>

                  <Button
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400"
                    onClick={copyFullPost}
                    data-testid="copy-full-post"
                  >
                    <Copy className="h-5 w-5 mr-2" />
                    Copy Full Post
                  </Button>

                  {mediaPreview && (
                    <Button
                      variant="outline"
                      className="w-full border-purple-500/50 text-purple-200 hover:bg-purple-500/20"
                      onClick={downloadMedia}
                      data-testid="download-media"
                    >
                      <Download className="h-5 w-5 mr-2" />
                      Download Media
                    </Button>
                  )}

                  {/* Platform Links */}
                  <div className="pt-4 border-t border-purple-500/20">
                    <p className="text-purple-200 text-sm mb-3 text-center">Open platform to post:</p>
                    <div className="grid grid-cols-5 gap-2">
                      {platforms.map((platform) => {
                        const IconComponent = typeof platform.icon === 'function' ? platform.icon : platform.icon;
                        return (
                          <Button
                            key={platform.id}
                            variant="outline"
                            className={`flex flex-col items-center gap-1 h-16 border-purple-500/30 hover:border-purple-400 ${
                              selectedPlatforms.includes(platform.id) ? 'bg-purple-500/20' : ''
                            }`}
                            onClick={() => openPlatform(platform.id)}
                            title={`Open ${platform.name}`}
                            data-testid={`open-${platform.id}`}
                          >
                            <div className={`${platform.color} p-1.5 rounded`}>
                              {typeof IconComponent === 'function' && platform.id === 'tiktok' ? (
                                <IconComponent />
                              ) : (
                                <IconComponent className="h-4 w-4 text-white" />
                              )}
                            </div>
                            <span className="text-[10px] text-purple-300">{platform.name}</span>
                            <ExternalLink className="h-3 w-3 text-purple-400" />
                          </Button>
                        );
                      })}
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    className="w-full text-purple-300 hover:text-white"
                    onClick={() => {
                      setShowPostSuccess(false);
                      navigate('/daily-drop');
                    }}
                    data-testid="back-to-dashboard"
                  >
                    Back to Dashboard
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DailyDropCreate;
